import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { ArrowRight, BookOpen, PlayCircle } from "lucide-react";
import AdminActions from "@/components/AdminActions";
import ShareButton from "@/components/ShareButton";
import { fontClass } from "@/lib/fonts";
import Seo from "@/components/Seo";

export default function Lessons() {
  const [lessons, setLessons] = useState([]);
  const [cat, setCat] = useState("All");

  const load = useCallback(() => {
    api.get("/lessons").then(r => setLessons(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const cats = useMemo(() => ["All", ...Array.from(new Set(lessons.map(l => l.category)))], [lessons]);
  const filtered = cat === "All" ? lessons : lessons.filter(l => l.category === cat);

  return (
    <div className="min-h-screen pt-28">
      <Seo title="Free AI Lessons" description="Step-by-step AI lessons with images and videos — learn practical AI from basics to workflows." path="/lessons" />
      <section className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="overline-label mb-4 flex items-center gap-2"><BookOpen size={13} /> Lessons — Learning Hub</div>
          <h1 className="font-display font-bold tracking-tight leading-[1.05] text-5xl sm:text-6xl lg:text-7xl text-white">
            Learn step by step,<br />
            <span className="text-gradient">the easy way.</span>
          </h1>
          <p className="text-slate-200 mt-6 max-w-2xl leading-relaxed">
            Every lesson explains the problem with images and videos — designed for absolute beginners. New lessons are added regularly.
          </p>

          <div className="mt-6"><AdminActions kind="lessons" onChange={load} /></div>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {cats.map(c => (
              <button
                key={c}
                onClick={() => setCat(c)}
                data-testid={`lessons-cat-${c.toLowerCase().replace(/\s/g, '-')}`}
                className={`text-sm font-semibold px-4 py-2 rounded-full transition-colors ${
                  cat === c
                    ? "bg-white text-[#04121A]"
                    : "bg-white/10 text-slate-200 hover:bg-white/20"
                }`}
              >
                {c}
                <span className={`ml-1.5 text-xs ${cat === c ? "text-slate-200" : "text-slate-200"}`}>
                  {c === "All" ? lessons.length : lessons.filter(l => l.category === c).length}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((l, i) => (
            <motion.div
              key={l.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
              className="relative"
            >
              <Link to={`/lessons/${l.slug}`} className="card-void overflow-hidden block group h-full flex flex-col" data-testid={`lesson-tile-${l.slug}`}>
                <div className="aspect-[16/9] overflow-hidden relative bg-white/10">
                  {l.cover_image ? (
                    <img src={l.cover_image} alt="" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="text-slate-200" size={36} />
                    </div>
                  )}
                  {l.video_url && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/25 group-hover:bg-slate-900/35 transition-colors">
                      <span className="w-14 h-14 rounded-full bg-white/95 shadow-lg flex items-center justify-center">
                        <PlayCircle className="text-orange-400" size={30} />
                      </span>
                    </div>
                  )}
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    <ShareButton url={`/api/share/lessons/${l.slug}`} testId={`share-lesson-tile-${l.slug}`} />
                    <AdminActions kind="lessons" item={l} onChange={load} mode="row" />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center justify-between mb-3">
                    <span className="chip">{l.category}</span>
                    <span className="text-xs text-slate-200">
                      {new Date(l.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </span>
                  </div>
                  <h2 className={`${fontClass(l.title_font, "font-bn")} font-bold text-lg text-white leading-snug group-hover:text-orange-300 transition-colors`}>
                    {l.title}
                  </h2>
                  <p className="mt-2.5 text-sm text-slate-200 font-bn leading-relaxed line-clamp-3 flex-1">{l.excerpt}</p>
                  <div className="mt-4 pt-4 border-t border-white/[0.07] text-orange-400 text-xs font-bold uppercase tracking-[0.1em] inline-flex items-center gap-1.5 group-hover:gap-2.5 transition-all">
                    Read Lesson <ArrowRight size={13} />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
        {filtered.length === 0 && (
          <div className="bg-white/[0.03] border border-dashed border-white/20 rounded-2xl p-16 text-center text-slate-200">
            No lessons in this category yet — check back soon.
          </div>
        )}
      </section>
    </div>
  );
}
